import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import './Menu.css';
import { fetchAvailableMenuItems, fetchMenuCategories } from '../../api/menuApi';
import FoodPreviewModal from '../../components/FoodPreviewModal/FoodPreviewModal.jsx';
import { useCart } from '../../context/CartContext.js';
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ProductCard from '../../components/ProductCard/ProductCard'
import translationMenu from '../../data/translationMenu'
import { Search, X, SlidersHorizontal, LayoutGrid, List } from 'lucide-react';
import Button from '../../components/UI/Button/Button';
import Pagination from '../../components/UI/Pagination/Pagination.jsx';
import LanguageContext from '../../context/LanguageContext.js';
import MenuStateIllustration from '../../components/MenuStateIllustration/MenuStateIllustration.jsx';
import {
    DEFAULT_MENU_PAGE_SIZE,
    MENU_PAGE_SIZE_OPTIONS,
    readMenuPageSize,
    writeMenuPageSize,
} from '../../utils/pageSizePreference.js';

const CATEGORY_ICONS = {
    pizza: '🍕',
    sandwich: '🥪',
    soup: '🍲',
    fish: '🐟',
    bread: "🥖",
    sushi: "🍣",
    pasta: '🍝',
    burger: '🍔',
    snack: '🍿',
    pastry: '🥐',
    dessert: '🍰',
    salad: '🥗',
    mainDishes: '🍖',
    vegetable: '🥬',
    icedTea: '𐃯',
    tea: '🍵',
    coldDrink: '🧊',
    smoothie: '🍹',
    milkshake: '🥤',
    hotCoffee: '☕',
    icedCoffee: '🧋',
};

function Menu() {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { addToCart, addToWishlist, isInWishlist } = useCart();

    /* ---- DATA STATE ---- */
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /* ---- FILTERING ENGINE ---- */
    const [activeFilter, setActiveFilter] = useState('All');
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [dietFilter, setDietFilter] = useState('all');
    const [showDietMenu, setShowDietMenu] = useState(false);
    const dietRef = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    
    /* ---- PAGINATION STATE ---- */
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(() => readMenuPageSize(DEFAULT_MENU_PAGE_SIZE));
    const itemsPerPageRef = useRef(itemsPerPage);
    itemsPerPageRef.current = itemsPerPage;

    /* ---- FETCH MENU FROM API ---- */
    const fetchMenu = useCallback(async (params = {}) => {
        try {
            setLoading(true);
            setError(null);
            const diet = params.diet ?? 'all';
            const dietParams = {};
            if (diet === 'vegetarian') dietParams.isVegetarian = true;
            else if (diet === 'non-vegetarian') dietParams.isVegetarian = false;
            else if (diet === 'vegan') dietParams.isVegan = true;
            else if (diet === 'non-vegan') dietParams.isVegan = false;

            const pageSize = params.limit || itemsPerPageRef.current;
            const data = await fetchAvailableMenuItems({
                page: params.page || 1,
                limit: pageSize,
                category: params.category === 'All' ? undefined : params.category,
                search: params.search || undefined,
                ...dietParams
            });
            const items = data.menuItems || data.data || [];
            const nextTotalItems = Number(data.pagination?.totalItems) || items.length;
            setMenuItems(items);
            setTotalItems(nextTotalItems);
            setTotalPages(
                data.pagination?.totalPages ||
                Math.ceil(nextTotalItems / pageSize) ||
                1
            );
            setCurrentPage(data.pagination?.currentPage || params.page || 1);
        } catch (err) {
            console.error('Error loading menu:', err);
            setError(err.message || 'Failed to load menu');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Fetch categories once
        const loadCategories = async () => {
            try {
                const data = await fetchMenuCategories();
                const drinkCategories = ['icedTea', 'tea', 'coldDrink', 'smoothie', 'milkshake', 'hotCoffee', 'icedCoffee'];
                const raw = data.categories || [];
                const food = raw.filter(c => !drinkCategories.includes(c));
                const drinks = drinkCategories.filter(c => raw.includes(c));
                setCategories([...food, ...drinks]);
            } catch (err) {
                console.error('Error loading categories:', err);
            }
        };
        loadCategories();

        // Fetch initial menu items
        fetchMenu({ page: 1, category: 'All', search: '', diet: 'all' });
    }, [fetchMenu]);

    const filterItems = useCallback((category) => {
        setActiveFilter(category);
        fetchMenu({ page: 1, category, search: searchQuery, diet: dietFilter });
    }, [fetchMenu, searchQuery, dietFilter]);

    const handleSearch = (event) => {
        const query = event.target.value;
        setSearchQuery(query);
        fetchMenu({ page: 1, category: activeFilter, search: query, diet: dietFilter });
    };

    const handleDietFilter = (diet) => {
        setDietFilter(diet);
        setShowDietMenu(false);
        fetchMenu({ page: 1, category: activeFilter, search: searchQuery, diet });
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dietRef.current && !dietRef.current.contains(e.target)) {
                setShowDietMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const scrollToCardGrid = () => {
        const el = document.getElementById('card-grid');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handlePageChange = (page) => {
        fetchMenu({ page, category: activeFilter, search: searchQuery, diet: dietFilter }).then(scrollToCardGrid);
    };

    const handlePageSizeChange = (nextSize) => {
        const size = Number.parseInt(nextSize, 10);
        if (!MENU_PAGE_SIZE_OPTIONS.includes(size)) return;
        writeMenuPageSize(size);
        setItemsPerPage(size);
        fetchMenu({
            page: 1,
            limit: size,
            category: activeFilter,
            search: searchQuery,
            diet: dietFilter,
        }).then(scrollToCardGrid);
    };

    // A search, category or diet filter is narrowing the list (vs. the menu itself being empty).
    const hasActiveFilters = activeFilter !== 'All' || searchQuery.trim() !== '' || dietFilter !== 'all';

    const showFullMenu = () => {
        setActiveFilter('All');
        setSearchQuery('');
        setDietFilter('all');
        fetchMenu({ page: 1, category: 'All', search: '', diet: 'all' });
    };

    const openPreview = (item) => setSelectedItem(item);
    const closePreview = () => setSelectedItem(null);

    const handleAddToCart = (item, event) => {
        event.stopPropagation();
        const variants = Array.isArray(item.variants) ? item.variants : [];
        if (variants.length > 0) {
            const v = variants.find((x) => x.isAvailable) || variants[0];
            addToCart({ ...item, selectedVariant: v, price: v.price });
            return;
        }
        addToCart(item);
    };

    const handleToggleWishlist = (item, event) => {
        event.stopPropagation();
        addToWishlist(item);
    };

    if (error) {
        return (
            <main className='main'>
                <Breadcrumb title={t('menu.title')}/>
                <div className='menu-container container menu-state-error' role="alert">
                    <MenuStateIllustration variant="error" />
                    <h2 className="menu-state-title">{t('menu.error', 'Failed to load menu')}</h2>
                    <p className="menu-state-text">{t('menu.errorDescription', 'Something went wrong on our side. Please try again.')}</p>
                    <Button
                        flex
                        onClick={() => window.location.reload()}
                        className="menu-state-error-retry"
                    >
                        {t('menu.retry', 'Try Again')}
                    </Button>
                </div>
            </main>
        );
    }

    return (
        <main className='main'>
            <Breadcrumb title={t('menu.title')}/>

            <div className='menu-container container' id='menu-container' dir={dir()}>

                {/* Search + Diet button */}
                <div className="menu-search-row">
                    <div className="menu-search">
                        <Search className="menu-search-icon" size={18} />
                        <input
                            type="text"
                            className="menu-search-input"
                            placeholder={t('menu.search')}
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        {searchQuery && (
                            <button
                                className="menu-search-clear"
                                onClick={() => { setSearchQuery(''); fetchMenu({ page: 1, category: activeFilter, search: '', diet: dietFilter }); }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="menu-diet-wrapper" ref={dietRef}>
                        <button
                            className={`menu-diet-trigger ${showDietMenu ? 'menu-diet-trigger--open' : ''} ${dietFilter !== 'all' ? 'menu-diet-trigger--filtered' : ''}`}
                            onClick={() => setShowDietMenu(prev => !prev)}
                        >
                            <SlidersHorizontal size={16} />
                        </button>

                        {showDietMenu && (
                            <div className="menu-diet-dropdown">
                                {[
                                    { key: 'all',            label: t('menu.dietAll', 'All'),            dot: '🍽️' },
                                    { key: 'vegetarian',     label: t('menu.vegetarian', 'Veg'),         cls: 'veg',      dot: '🟢' },
                                    { key: 'non-vegetarian', label: t('menu.nonVegetarian', 'Non-Veg'),  cls: 'nonveg',   dot: '🔴' },
                                    { key: 'vegan',          label: t('menu.vegan', 'Vegan'),             cls: 'vegan',    dot: '🌱' },
                                    { key: 'non-vegan',      label: t('menu.nonVegan', 'Non-Vegan'),      cls: 'nonvegan', dot: '🍖' },
                                ].map(d => (
                                    <button
                                        key={d.key}
                                        className={`menu-diet-dropdown-item${d.cls ? ` menu-diet-dropdown-item--${d.cls}` : ''} ${dietFilter === d.key ? 'menu-diet-dropdown-item--active' : ''}`}
                                        onClick={() => handleDietFilter(d.key)}
                                    >
                                        <span className="menu-diet-dropdown-dot">{d.dot}</span>
                                        <span>{d.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                </div>

                {/* Categories */}
                <div className="menu-categories" id='card-grid'>
                    <button
                        className={`menu-cat ${activeFilter === 'All' ? 'menu-cat--active' : ''}`}
                        onClick={() => filterItems('All')}
                    >

                        <span className="menu-cat-circle">🍽️</span>
                        <span className="menu-cat-text">{t('menu.all')}</span>
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            className={`menu-cat ${activeFilter === cat ? 'menu-cat--active' : ''}`}
                            onClick={() => filterItems(cat)}
                        >
                            <span className="menu-cat-circle">{CATEGORY_ICONS[cat] || '🍴'}</span>
                            <span className="menu-cat-text">{translationMenu[currentLanguage]?.categories?.[cat] || cat}</span>
                        </button>
                    ))}
                </div>

                <div className="menu-view-row">
                    <div className="menu-view-switch" role="group" aria-label={t('menu.viewMode', 'View mode')}>
                        <button
                            className={`menu-view-switch-button ${viewMode === 'grid' ? 'menu-view-switch-button--active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title={t('menu.gridView', 'Grid view')}
                            aria-label={t('menu.gridView', 'Grid view')}
                            aria-pressed={viewMode === 'grid'}
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button
                            className={`menu-view-switch-button ${viewMode === 'list' ? 'menu-view-switch-button--active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title={t('menu.listView', 'List view')}
                            aria-label={t('menu.listView', 'List view')}
                            aria-pressed={viewMode === 'list'}
                        >
                            <List size={16} />
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className='menu-container container menu-state-loading'>
                        <div>
                            <div className="menu-loading-spinner"></div>
                            <p className="menu-state-loading-text">{t('menu.loading', 'Loading menu...')}</p>
                        </div>
                    </div>
                )}

                {!loading && menuItems.length === 0 ? (
                    <div className="menu-state-empty" role="status">
                        <MenuStateIllustration variant={hasActiveFilters ? 'noResults' : 'empty'} />
                        <h2 className="menu-state-title">
                            {hasActiveFilters
                                ? t('menu.noMatchesTitle', 'No dishes found')
                                : t('menu.emptyTitle', 'No dishes yet')}
                        </h2>
                        <p className="menu-state-text">
                            {hasActiveFilters
                                ? t('menu.noMatchesDescription', 'Nothing matches your search or filters. Try something else or browse the full menu.')
                                : t('menu.emptyDescription', "Our menu is being prepared. Please check back soon!")}
                        </p>
                        {hasActiveFilters && (
                            <Button flex onClick={showFullMenu} className="menu-state-action">
                                {t('menu.showFullMenu', 'Show full menu')}
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className={`product-container ${viewMode === 'list' ? 'product-container--list' : ''}`} >
                        {menuItems.map(item => (
                            <ProductCard
                                key={item._id || item.id}
                                item={item}
                                currentLanguage={currentLanguage}
                                t={t}
                                onPreview={openPreview}
                                onAddToCart={handleAddToCart}
                                onToggleWishlist={handleToggleWishlist}
                                isInWishlist={isInWishlist}
                            />
                            ))}
                    </div>
                )}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    pageSizeOptions={MENU_PAGE_SIZE_OPTIONS}
                    dir={dir()}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            <FoodPreviewModal
                item={selectedItem}
                isOpen={Boolean(selectedItem)}
                onClose={closePreview}
                currentLanguage={currentLanguage}
                t={t}
            />
        </main>
    );
}

export default Menu;
